import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";

interface FeedbackPopupProps {
  open: boolean;
  onClose: () => void;
}

export default function FeedbackPopup({ open, onClose }: FeedbackPopupProps) {
  const [formData, setFormData] = useState({
    feedbackType: "Bug",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { t } = useTranslation();

  const handleChange = (e: { target: { name: string; value: string } }) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await fetch("/api/submitFeedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.success) {
        setShowSuccess(true);
        setFormData({ feedbackType: "Bug", subject: "", message: "" });
        setTimeout(() => onClose(), 1500);
      }
    } catch {
      // silently fail
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog id="feedback-dialog" open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>{t("feedback.title")}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t("feedback.description")}
          </Typography>
          <FormControl id="feedback-type" fullWidth margin="dense">
            <InputLabel>{t("feedback.type")}</InputLabel>
            <Select
              name="feedbackType"
              value={formData.feedbackType}
              onChange={handleChange}
              label={t("feedback.type")}
            >
              <MenuItem value="Bug">{t("feedback.bugReport")}</MenuItem>
              <MenuItem value="Suggestion">{t("feedback.suggestion")}</MenuItem>
              <MenuItem value="General">{t("feedback.general")}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            id="feedback-subject"
            autoFocus
            margin="dense"
            label={t("feedback.subject")}
            name="subject"
            fullWidth
            variant="outlined"
            value={formData.subject}
            onChange={handleChange}
            inputProps={{ maxLength: 255 }}
          />
          <TextField
            id="feedback-message"
            margin="dense"
            label={t("feedback.message")}
            name="message"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={formData.message}
            onChange={handleChange}
            inputProps={{ maxLength: 5000 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("feedback.cancel")}</Button>
          <Button
            id="feedback-submit-btn"
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.subject.trim() || !formData.message.trim() || submitting}
          >
            {t("feedback.submit")}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={showSuccess} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity="success" variant="filled">
          {t("feedback.success")}
        </Alert>
      </Snackbar>
    </>
  );
}
